# Nutrition: Diet, Portions & Feeding Habits

What your pet eats shapes their energy, weight, coat, and long-term health. Consistent portions, quality ingredients, and regular mealtimes make the biggest difference.

<ArticleImage
  src="/images/guide/food-pet-bowl.jpg"
  alt="Dog eating from their food bowl"
  caption="Measured portions and consistent mealtimes matter as much as ingredients."
/>

---

<Section emoji="⚖️" title="Portions & Body Condition">

<Tip label="Measure meals" text="Measure every meal with a scoop or kitchen scale instead of estimating by eye. Small daily overfeeds add up quickly over months." />
<Tip label="Check the shape" text="You should be able to feel ribs easily with light pressure, and see a gentle waist from above. A monthly body-shape check catches drift before it becomes a bigger problem." />
<Tip label="Treat budget" text="Keep treats below roughly 10% of daily calories so meals stay nutritionally balanced. If treat use rises during training, reduce meal portions slightly to compensate." />

</Section>

<ArticleImage
  src="/images/guide/food-rabbit.jpg"
  alt="Rabbit eating fresh greens"
  caption="Choose a diet suited to your pet’s species, life stage, and any health needs."
/>

<Section emoji="🥩" title="Choose the Right Diet">

<Tip label="Life stage" text="Puppies, adults, and seniors need different nutrient balance and calorie density. Feeding for the current life stage supports growth, maintenance, and healthy aging." />
<Tip label="Health needs" text="Kidney disease, allergies, obesity, and GI sensitivity often require targeted diets. Vet-guided nutrition can reduce flare-ups and improve day-to-day comfort." />
<Tip label="Consistency" text="Keep formulas stable whenever possible to support digestion and stool quality. Frequent food switching can trigger avoidable stomach upset." />

<Callout>
If you’re considering a raw or home-prepared diet, check in with your vet to ensure it’s complete and safe.
</Callout>

</Section>

<ArticleImage
  src="/images/guide/food-pet-water.jpg"
  alt="Cat drinking water from a bowl"
  caption="Hydration supports digestion and urinary health — refresh water bowls daily."
/>

<Section emoji="💧" title="Hydration & Smart Treats">

<Tip label="Fresh water" text="Refresh water at least daily and clean bowls often to reduce biofilm buildup. In multi-pet homes, place several bowls in different rooms to improve access." />
<Tip label="Cats need moisture" text="Cats naturally drink less than many species, so moisture from wet food can be very helpful. Fountains and wide bowls can also encourage steadier intake." />
<Tip label="Foods to avoid" text="Keep chocolate, grapes/raisins, onions, garlic, and xylitol products fully out of reach. Even small amounts can be toxic depending on the pet and dose." />

</Section>

<Section emoji="🔄" title="Switching Foods Safely">

<Tip label="7–10 day transition" text="Blend old and new food over 7–10 days, starting with mostly old and slowly increasing the new formula. Sensitive pets may need an even slower pace." />
<Tip label="Watch stool quality" text="Loose stool, gassiness, or appetite dips often mean transition speed is too fast. Step back to the previous ratio for a day or two, then continue gradually." />
<Tip label="One change at a time" text="Change only one nutrition variable at once — food, treats, or supplements. This makes it far easier to spot what helps or causes issues." />

</Section>

<Section emoji="🏷️" title="Reading Labels Like a Pro">

<Tip label="Ingredient order" text="Ingredients are listed by pre-cooked weight, so water-heavy ingredients may appear higher than expected. Look for clearly named protein sources and avoid vague labeling where possible." />
<Tip label="Guaranteed analysis" text="Compare protein, fat, fiber, and moisture to understand how formulas differ. For fair comparisons across wet and dry foods, use dry-matter calculations when needed." />
<Tip label="Calorie density" text="Always check kcal per cup or can before setting portions. The same volume can vary widely in calories between brands and formulas." />

<Callout>
For pets with chronic conditions (kidney disease, diabetes, severe allergies), ask your vet for exact calorie and nutrient targets.
</Callout>

</Section>

<Disclaimer title="⚠️ Friendly Reminder">
This guide is educational and not a substitute for veterinary advice. Sudden appetite changes, vomiting, or diarrhea should always be discussed with your vet.
</Disclaimer>
